import { useCMS } from '../../../contexts/CMSContext';
import EditableText from '../../../components/cms/EditableText';
import EditableImage from '../../../components/cms/EditableImage';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import type { ServiceItem } from '../../../types/cms';
import { getEffectiveServiceItem } from '../../../utils/cmsMobile';

interface ServiceCardProps {
  service: ServiceItem;
  index: number;
  onRequestBooking: (service: ServiceItem) => void;
}

function ServiceCard({ service, index, onRequestBooking }: ServiceCardProps) {
  const { isEditMode, removeService, updateService, data, isMobileContentActive } = useCMS();
  const display = getEffectiveServiceItem(data, service.id, isMobileContentActive) ?? service;
  const isFeatured = index === 0;

  const handleEditField = (field: keyof ServiceItem, value: string) => {
    updateService(service.id, field, value);
  };

  return (
    <div
      className={`relative rounded-2xl overflow-hidden flex flex-col group ${
        isFeatured
          ? 'bg-[#2D3B29] text-white'
          : 'bg-white border border-stone-100'
      }`}
    >
      {isEditMode && (
        <button
          onClick={() => removeService(service.id)}
          className="absolute top-3 right-3 z-20 w-7 h-7 flex items-center justify-center bg-red-500 text-white rounded-full hover:bg-red-600 cursor-pointer shadow"
          title="Eliminar servicio"
        >
          <i className="ri-close-line text-sm"></i>
        </button>
      )}

      <EditableImage
        imageKey={`service_${service.id}`}
        alt={display.title}
        wrapperClassName="w-full overflow-hidden h-40"
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
      />

      {isFeatured && (
        <span className="absolute top-3 left-3 z-10 inline-flex items-center gap-1.5 bg-[#8B9D83]/90 text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full w-fit">
          <i className="ri-star-line text-[10px]"></i>
          Destacado
        </span>
      )}

      <div className={`flex flex-col gap-3 p-5 flex-1 ${isFeatured ? '' : ''}`}>
        <div>
          {isEditMode ? (
            <input
              className={`font-semibold text-lg w-full bg-transparent border-b-2 border-dashed border-[#8B9D83]/50 outline-none pb-1 ${isFeatured ? 'text-white' : 'text-[#1A1A1A]'}`}
              value={display.title}
              onChange={(e) => handleEditField('title', e.target.value)}
            />
          ) : (
            <h3 className={`font-semibold text-lg ${isFeatured ? 'text-white' : 'text-[#1A1A1A]'}`}>
              {display.title}
            </h3>
          )}
        </div>

        <div>
          {isEditMode ? (
            <textarea
              className={`text-sm w-full bg-transparent border border-dashed border-[#8B9D83]/50 rounded p-1 outline-none resize-none leading-relaxed ${isFeatured ? 'text-stone-300' : 'text-[#6B6B6B]'}`}
              value={display.description}
              rows={3}
              onChange={(e) => handleEditField('description', e.target.value)}
            />
          ) : (
            <p className={`text-sm leading-relaxed ${isFeatured ? 'text-stone-300' : 'text-[#6B6B6B]'}`}>
              {display.description}
            </p>
          )}
        </div>

        <div className="flex items-center justify-between mt-auto pt-3 border-t border-white/10">
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className={`ri-time-line text-xs ${isFeatured ? 'text-stone-400' : 'text-[#6B6B6B]'}`}></i>
            </div>
            {isEditMode ? (
              <input
                className={`text-xs bg-transparent border-b border-dashed border-[#8B9D83]/40 outline-none w-24 ${isFeatured ? 'text-stone-300' : 'text-[#6B6B6B]'}`}
                value={display.duration}
                onChange={(e) => handleEditField('duration', e.target.value)}
              />
            ) : (
              <span className={`text-xs ${isFeatured ? 'text-stone-300' : 'text-[#6B6B6B]'}`}>{display.duration}</span>
            )}
          </div>

          <div>
            {isEditMode ? (
              <input
                className={`font-bold text-sm bg-transparent border-b border-dashed border-[#8B9D83]/40 outline-none w-20 text-right ${isFeatured ? 'text-white' : 'text-[#1A1A1A]'}`}
                value={display.price}
                onChange={(e) => handleEditField('price', e.target.value)}
              />
            ) : (
              <span className={`font-bold text-sm ${isFeatured ? 'text-white' : 'text-[#1A1A1A]'}`}>{display.price}</span>
            )}
          </div>
        </div>

        <button
          type="button"
          onClick={() => onRequestBooking(display)}
          className={`inline-flex items-center justify-center gap-2 text-sm font-semibold px-4 py-2.5 rounded-full transition-all cursor-pointer whitespace-nowrap ${
            isFeatured
              ? 'bg-[#8B9D83] text-white hover:bg-[#6B7D63]'
              : 'bg-[#1A1A1A] text-white hover:bg-[#333]'
          }`}
        >
          Pedir cita
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-arrow-right-line text-xs"></i>
          </div>
        </button>
      </div>
    </div>
  );
}

interface ServicesSectionProps {
  onRequestBooking: (service: ServiceItem) => void;
}

export default function ServicesSection({ onRequestBooking }: ServicesSectionProps) {
  const { data, isEditMode, addService } = useCMS();
  const { services } = data;
  const { ref, isVisible } = useScrollAnimation();

  return (
    <section id="servicios" className="bg-[#EDE8E1] py-28">
      <div
        ref={ref}
        className={`max-w-7xl mx-auto px-6 transition-all duration-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}
      >
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <span className="w-1 h-4 bg-[#8B9D83] rounded-full inline-block"></span>
              <EditableText
                section="services"
                field="label"
                tag="span"
                className="text-xs font-bold uppercase tracking-widest text-[#8B9D83]"
              />
            </div>
            <EditableText
              section="services"
              field="title"
              tag="h2"
              className="font-serif text-5xl font-semibold text-[#1A1A1A] leading-tight"
            />
          </div>
          <div className="flex items-center gap-4">
            <EditableText
              section="services"
              field="subtitle"
              tag="p"
              multiline
              className="text-[#6B6B6B] text-base max-w-xs text-right"
            />
            {isEditMode && (
              <button
                onClick={addService}
                className="flex items-center gap-2 bg-[#8B9D83] text-white text-sm font-semibold px-4 py-2 rounded-full hover:bg-[#6B7D63] transition-colors cursor-pointer whitespace-nowrap shrink-0"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-add-line text-sm"></i>
                </div>
                Agregar
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
          {services.items.map((service, i) => (
            <ServiceCard
              key={service.id}
              service={service}
              index={i}
              onRequestBooking={onRequestBooking}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
